import { useCallback, useEffect, useState } from 'react'
import {
  platformHealthApi,
  type CacheStatus,
  type CategoryMetrics,
  type JobsHealthSummary,
  type MetricsWindow,
  type OutboxHealthSummary,
  type PlatformHealthReport,
  type PlatformMetrics,
} from '../api/platformHealth'
import {
  reportingAggregatesApi,
  type LeadFunnelReconciliation,
  type ReportingAggregatesStatus,
} from '../api/reportingAggregates'
import {
  SEARCH_ENTITY_LABELS,
  searchIndexApi,
  type SearchIndexStatus,
} from '../api/searchIndex'
import {
  Page,
  PageHeader,
  FilterBar,
  Button,
  AccessDenied,
  Callout,
  LoadingState,
  Card,
  CardHeader,
  CardContent,
  DataTable,
  StatusPill,
  Select,
  StatCard,
  TextField,
  type PillTone,
  StatGrid,
  CardGrid,
  KeyValueList,
} from '../components/ui'

/**
 * F-073 (TASK-0327, REQ-302-001/002/003/006/009/014, REQ-276-001): salute della piattaforma (solo Admin).
 * Mostra tempi P95 per categoria contro gli obiettivi PROVVISORI del §276, error rate e successo API,
 * latenza delle dashboard, disponibilita' e rotte piu' lente, piu' gli indicatori gia' esistenti
 * (database, disco, backup, job, outbox). Le metriche di latenza sono in memoria e si azzerano al riavvio;
 * la disponibilita' vale solo dall'avvio del processo. Nessuna cifra economica.
 */
const WINDOWS: { key: MetricsWindow; label: string }[] = [
  { key: '15m', label: 'Ultimi 15 minuti' },
  { key: '1h', label: 'Ultima ora' },
  { key: '24h', label: 'Ultime 24 ore' },
]

const BACKUP_LABELS: Record<string, string> = {
  ok: 'Regolare',
  late: 'In ritardo',
  missing: 'Nessun backup riuscito',
  directoryMissing: 'Cartella dei backup assente',
  unreadable: 'Stato non leggibile',
  unavailable: 'Non disponibile',
}

function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

function formatDateTime(s: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function ms(v: number | null): string {
  if (v === null) return '—'
  return `${Math.round(v).toLocaleString('it-IT')} ms`
}

function pct(v: number | null, digits = 2): string {
  if (v === null) return '—'
  return `${(v * 100).toLocaleString('it-IT', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`
}

function bytes(v: number | null): string {
  if (v === null) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let n = v
  let i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toLocaleString('it-IT', { maximumFractionDigits: 1 })} ${units[i]}`
}

function ageText(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds < 90) return `${Math.round(seconds)} s`
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`
  return `${(seconds / 3600).toLocaleString('it-IT', { maximumFractionDigits: 1 })} h`
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string | undefined; tone?: 'ok' | 'bad' | 'muted' }) {
  return (
    <StatCard label={label} value={value} supporting={sub} tone={tone === 'ok' ? 'success' : tone === 'bad' ? 'error' : 'neutral'} />
  )
}

function targetBadge(c: CategoryMetrics) {
  if (c.withinTarget === null) return <StatusPill tone="neutral">nessun dato</StatusPill>
  return c.withinTarget
    ? <StatusPill tone="success">nell'obiettivo</StatusPill>
    : <StatusPill tone="error">oltre l'obiettivo</StatusPill>
}

const TABLE_LABELS: Record<string, string> = {
  daily_lead_funnel: 'Funnel lead giornaliero',
  daily_call_center_operator_activity: 'Attività operatori call center',
  daily_agent_performance: 'Prestazioni agenti (contratti)',
}

/** Oggi nel calendario di Roma, "YYYY-MM-DD": il giorno degli aggregati dei lead e' quello di Roma. */
function todayInRome(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date())
}

/**
 * F-090 (REQ-278-001, REQ-279-001): stato degli aggregati di reporting, riconciliazione con il calcolo dal vivo e
 * ricalcolo di un giorno. Solo conteggi, nessun importo. Il ricalcolo accoda un job: il risultato compare dopo
 * qualche istante, e "Aggiorna" lo mostra.
 */
function ReportingAggregatesSection() {
  const [status, setStatus] = useState<ReportingAggregatesStatus | null>(null)
  const [day, setDay] = useState(todayInRome())
  const [rec, setRec] = useState<LeadFunnelReconciliation | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await reportingAggregatesApi.status())
      setError(null)
    } catch {
      setStatus(null)
      setError('Lo stato degli aggregati non è disponibile in questo momento.')
    }
  }, [])

  useEffect(() => { void loadStatus() }, [loadStatus])

  async function reconcile() {
    setBusy(true)
    setMessage(null)
    try {
      setRec(await reportingAggregatesApi.reconcile(day))
      setError(null)
    } catch (e) {
      setRec(null)
      setError(e instanceof Error ? e.message : 'Riconciliazione non riuscita.')
    } finally {
      setBusy(false)
    }
  }

  async function recalculate() {
    setBusy(true)
    setMessage(null)
    try {
      await reportingAggregatesApi.recalculate(day)
      setMessage(`Ricalcolo del ${day} in coda. Aggiorna tra qualche istante per vedere l'esito.`)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ricalcolo non riuscito.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined" padding="none" data-testid="reporting-aggregates">
      <CardHeader
        title="Aggregati di reporting"
        actions={
          <Button variant="outlined" size="sm" onClick={() => void loadStatus()} disabled={busy}>
            Aggiorna
          </Button>
        }
      />
      <CardContent>
        <div className="md-typescale-body-small d-flex flex-column gap-3">
          {error && <Callout tone="warning">{error}</Callout>}
          {message && <Callout tone="success">{message}</Callout>}
          {status && (
            <>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <StatusPill tone={status.schedulerEnabled ? 'success' : 'neutral'}>
                  Calcolo periodico {status.schedulerEnabled ? 'acceso' : 'spento'}
                </StatusPill>
                <StatusPill tone={status.readFromAggregates ? 'success' : 'neutral'}>
                  Lettura dagli aggregati {status.readFromAggregates ? 'accesa' : 'spenta'}
                </StatusPill>
                <span className="text-muted">
                  Giorni calcolati: {status.calculatedDays.count}
                  {status.calculatedDays.from && status.calculatedDays.to
                    ? ` (dal ${status.calculatedDays.from} al ${status.calculatedDays.to})` : ''}
                  {status.calculatedDays.lastComputedAt ? `, ultimo calcolo ${formatDateTime(status.calculatedDays.lastComputedAt)}` : ''}
                </span>
              </div>
              <DataTable variant="plain">
                <thead>
                  <tr><th>Tabella</th><th className="text-end">Righe</th><th className="text-end">Giorni coperti</th><th>Ultimo calcolo</th></tr>
                </thead>
                <tbody>
                  {status.tables.map(t => (
                    <tr key={t.table}>
                      <td>{TABLE_LABELS[t.table] ?? t.table}</td>
                      <td className="text-end md-num">{t.rows.toLocaleString('it-IT')}</td>
                      <td className="text-end md-num">{t.daysCovered.toLocaleString('it-IT')}</td>
                      <td>{formatDateTime(t.lastComputedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              <div className="text-muted">{status.note}</div>
            </>
          )}

          <div className="d-flex flex-wrap align-items-start gap-2">
            <TextField
              id="aggregates-day"
              label="Giorno (Roma)"
              type="date"
              value={day}
              max={todayInRome()}
              onChange={e => setDay(e.target.value)}
            />
            <Button variant="outlined" onClick={() => void reconcile()} disabled={busy || !day}>
              Riconcilia con il calcolo dal vivo
            </Button>
            <Button variant="tonal" onClick={() => void recalculate()} disabled={busy || !day}>
              Ricalcola il giorno
            </Button>
          </div>

          {rec && (
            <div data-testid="reconciliation-result">
              {!rec.calculated ? (
                <Callout tone="warning">Il giorno {rec.day} non è ancora stato calcolato: non c'è nulla da confrontare.</Callout>
              ) : rec.matches ? (
                <Callout tone="success">
                  Il {rec.day} l'aggregato coincide con il calcolo dal vivo ({rec.aggregateRows} righe, nessuna differenza).
                </Callout>
              ) : (
                <div className="d-flex flex-column gap-2">
                  <Callout tone="error">
                    Il {rec.day} ci sono {rec.differences.length} differenze fra aggregato e calcolo dal vivo. Ricalcola il giorno.
                  </Callout>
                  <ul className="mb-0">
                    {rec.differences.map((d, i) => (
                      <li key={`${d.kind}-${d.key}-${d.field}-${i}`}>
                        {d.kind === 'funnel' ? 'Funnel' : 'Operatore'} {d.key}, {d.field}: aggregato {d.aggregate ?? '—'}, dal vivo {d.live ?? '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

const CACHE_REGION_LABELS: Record<string, string> = {
  catalog: 'Catalogo e ruoli',
  settings: 'Impostazioni e flag',
  dashboards: 'Cruscotti',
}

/**
 * F-107 (REQ-280-001): stato della cache in memoria per dati di riferimento e cruscotti. La cache non è mai fonte
 * ufficiale dei dati: serve solo a rendere più rapide le letture e scade da sola. "Svuota" è riservato all'Admin
 * (l'intera pagina lo è) e ricostruisce i dati dal database alla richiesta successiva. Solo contatori.
 */
function CacheSection() {
  const [status, setStatus] = useState<CacheStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setStatus(await platformHealthApi.cache())
      setError(null)
    } catch {
      setStatus(null)
      setError('Lo stato della cache non è disponibile in questo momento.')
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function clear(region?: string) {
    setBusy(true)
    setMessage(null)
    try {
      await platformHealthApi.clearCache(region)
      setMessage(region ? `Cache "${CACHE_REGION_LABELS[region] ?? region}" svuotata.` : 'Cache svuotata.')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Svuotamento non riuscito.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined" padding="none" data-testid="cache-status">
      <CardHeader
        title="Cache"
        actions={
          <div className="d-flex gap-2">
            <Button variant="outlined" size="sm" onClick={() => void load()} disabled={busy}>
              Aggiorna
            </Button>
            <Button variant="outlined" size="sm" onClick={() => void clear()} disabled={busy || !status}>
              Svuota tutto
            </Button>
          </div>
        }
      />
      <CardContent>
        <div className="md-typescale-body-small d-flex flex-column gap-3">
          {error && <Callout tone="warning">{error}</Callout>}
          {message && <Callout tone="success">{message}</Callout>}
          {status && (
            <>
              <div className="d-flex flex-wrap gap-2">
                <StatusPill tone={status.referenceDataEnabled ? 'success' : 'neutral'}>
                  Dati di riferimento {status.referenceDataEnabled ? 'in cache' : 'non in cache'} ({status.referenceDataTtlSeconds} s)
                </StatusPill>
                <StatusPill tone={status.dashboardsEnabled ? 'success' : 'neutral'}>
                  Cruscotti {status.dashboardsEnabled ? 'in cache' : 'non in cache'} ({status.dashboardsTtlSeconds} s)
                </StatusPill>
              </div>
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Regione</th><th className="text-end">Voci</th><th className="text-end">Richieste servite dalla cache</th>
                    <th className="text-end">Hit rate</th><th>Ultima invalidazione</th><th />
                  </tr>
                </thead>
                <tbody>
                  {status.regions.map(r => (
                    <tr key={r.region}>
                      <td>{CACHE_REGION_LABELS[r.region] ?? r.region}</td>
                      <td className="text-end md-num">{r.entries}</td>
                      <td className="text-end md-num">{r.hits.toLocaleString('it-IT')} su {(r.hits + r.misses).toLocaleString('it-IT')}</td>
                      <td className="text-end md-num">{r.hitRate === null ? '—' : `${(r.hitRate * 100).toFixed(1).replace('.', ',')}%`}</td>
                      <td>{formatDateTime(r.lastInvalidatedAt)}</td>
                      <td className="text-end">
                        <Button variant="outlined" size="sm" onClick={() => void clear(r.region)} disabled={busy}>
                          Svuota
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              <div className="text-muted">
                La cache non è mai la fonte dei dati: le scritture e i controlli di accesso leggono sempre dal database.
                I contatori si azzerano al riavvio. Spenta, il comportamento è quello di sempre.
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

const SEARCH_INDEX_TONES: Record<string, PillTone> = {
  Pronto: 'success',
  InCostruzione: 'info',
  Errore: 'error',
  Spento: 'neutral',
}

/**
 * F-124 (REQ-244-001): stato dell'indice di ricerca dedicato (schema `search`, separato dalle tabelle di lavoro).
 * L'indice propone i candidati, il database li conferma: i permessi e i risultati restano quelli di sempre. Solo
 * conteggi e date. "Ricostruisci" chiede una ricostruzione completa, che parte al giro successivo del lavoratore.
 */
function SearchIndexSection() {
  const [status, setStatus] = useState<SearchIndexStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setStatus(await searchIndexApi.status())
      setError(null)
    } catch {
      setStatus(null)
      setError("Lo stato dell'indice di ricerca non è disponibile in questo momento.")
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function rebuild() {
    setBusy(true)
    setMessage(null)
    try {
      const res = await searchIndexApi.rebuild()
      setMessage(res.available
        ? (res.workerEnabled
          ? 'Ricostruzione richiesta: parte al prossimo giro del lavoratore.'
          : 'Ricostruzione richiesta, ma il lavoratore dell’indice è spento: non partirà finché non viene acceso.')
        : 'Le tabelle dell’indice non esistono ancora: la migration deve essere applicata.')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Richiesta non riuscita.')
    } finally {
      setBusy(false)
    }
  }

  const lag = status?.lagSeconds
  return (
    <Card variant="outlined" padding="none" data-testid="search-index-status">
      <CardHeader
        title="Indice di ricerca"
        actions={
          <div className="d-flex gap-2">
            <Button variant="outlined" size="sm" onClick={() => void load()} disabled={busy}>
              Aggiorna
            </Button>
            <Button variant="outlined" size="sm" onClick={() => void rebuild()} disabled={busy || !status?.available}>
              Ricostruisci
            </Button>
          </div>
        }
      />
      <CardContent>
        <div className="md-typescale-body-small d-flex flex-column gap-3">
          {error && <Callout tone="warning">{error}</Callout>}
          {message && <Callout tone="success">{message}</Callout>}
          {status && !status.available && (
            <Callout tone="info">
              L’indice di ricerca non è ancora disponibile: le tabelle verranno create con la migration.
            </Callout>
          )}
          {status?.available && (
            <>
              <div className="d-flex flex-wrap gap-2">
                <StatusPill tone={SEARCH_INDEX_TONES[status.status ?? 'Spento'] ?? 'neutral'}>
                  {status.status ?? 'Spento'}
                </StatusPill>
                <StatusPill tone={status.workerEnabled ? 'success' : 'neutral'}>
                  Lavoratore {status.workerEnabled ? 'acceso' : 'spento'}
                </StatusPill>
                <StatusPill tone={status.useForQueries ? (status.usable ? 'success' : 'warning') : 'neutral'}>
                  {status.useForQueries
                    ? (status.usable ? 'La ricerca usa l’indice' : 'Ricerca sul percorso di sempre (indice non pronto o in ritardo)')
                    : 'La ricerca non usa l’indice'}
                </StatusPill>
              </div>
              <DataTable variant="plain">
                <thead>
                  <tr><th>Tipo</th><th className="text-end">Voci</th></tr>
                </thead>
                <tbody>
                  {Object.entries(status.rowsByType ?? {}).map(([type, rows]) => (
                    <tr key={type}>
                      <td>{SEARCH_ENTITY_LABELS[type] ?? type}</td>
                      <td className="text-end md-num">{rows.toLocaleString('it-IT')}</td>
                    </tr>
                  ))}
                  <tr className="md-typescale-emphasized-body-medium">
                    <td>Totale</td>
                    <td className="text-end md-num">{(status.totalRows ?? 0).toLocaleString('it-IT')}</td>
                  </tr>
                </tbody>
              </DataTable>
              <KeyValueList
                columns={2}
                items={[
                  { label: 'Ultimo aggiornamento', value: formatDateTime(status.lastIncrementalAt ?? null) },
                  { label: 'Ultima ricostruzione', value: formatDateTime(status.lastFullRebuildAt ?? null) },
                  {
                    label: 'Ritardo',
                    value: (
                      <>
                        {lag === null || lag === undefined ? '—' : lag < 120 ? `${Math.round(lag)} s` : `${Math.round(lag / 60)} min`}
                        {status.maxLagMinutes ? ` (oltre ${status.maxLagMinutes} min la ricerca torna al percorso di sempre)` : ''}
                      </>
                    ),
                  },
                  { label: 'Ricostruzione richiesta', value: status.rebuildRequestedAt ? formatDateTime(status.rebuildRequestedAt) : 'no' },
                ]}
              />
              {status.lastError && <Callout tone="error">Ultimo errore: {status.lastError}</Callout>}
              <div className="text-muted">
                L’indice contiene solo i campi che la ricerca già interroga, nessun importo. Il database conferma sempre i
                risultati; spento o in ritardo, la ricerca è quella di sempre.
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function PlatformHealthPage() {
  const [win, setWin] = useState<MetricsWindow>('1h')
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null)
  const [report, setReport] = useState<PlatformHealthReport | null>(null)
  const [jobs, setJobs] = useState<JobsHealthSummary | null>(null)
  const [outbox, setOutbox] = useState<OutboxHealthSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Ogni sezione puo' fallire da sola: una sonda lenta o rotta non oscura le altre.
    const results = await Promise.allSettled([
      platformHealthApi.metrics(win),
      platformHealthApi.report(),
      platformHealthApi.jobs(),
      platformHealthApi.outbox(),
    ])
    const [m, r, j, o] = results
    if (results.some(x => x.status === 'rejected' && isForbidden(x.reason))) {
      setUnauthorized(true)
      setLoading(false)
      return
    }
    setUnauthorized(false)
    setMetrics(m.status === 'fulfilled' ? m.value : null)
    setReport(r.status === 'fulfilled' ? r.value : null)
    setJobs(j.status === 'fulfilled' ? j.value : null)
    setOutbox(o.status === 'fulfilled' ? o.value : null)
    const failed = results.filter(x => x.status === 'rejected').length
    if (failed > 0) setError(`${failed} sezioni su 4 non sono disponibili in questo momento.`)
    setLoading(false)
  }, [win])

  useEffect(() => { void load() }, [load])

  const dashboard = metrics?.categories.find(c => c.category === 'Dashboard') ?? null
  const writes = metrics?.categories.find(c => c.category === 'ApiInterne') ?? null
  // F-112: le API esterne (/api/v1) hanno una categoria propria.
  const external = metrics?.categories.find(c => c.category === 'ApiEsterne') ?? null
  const totalRequests = metrics?.categories.reduce((s, c) => s + c.requests, 0) ?? 0

  return (
    <Page>
      <PageHeader
        title="Salute piattaforma"
        icon="monitor_heart"
        actions={
          <Button variant="outlined" onClick={() => void load()} disabled={loading}>
            Aggiorna
          </Button>
        }
      />

      <FilterBar>
        <Select
          label="Finestra di osservazione"
          value={win}
          onChange={e => setWin(e.target.value as MetricsWindow)}
        >
          {WINDOWS.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
        </Select>
      </FilterBar>

      {unauthorized && <AccessDenied requirement="il ruolo Admin" />}
      {!unauthorized && error && <Callout tone="warning">{error}</Callout>}
      {loading && !metrics && <LoadingState />}

      {!unauthorized && metrics && (
        <>
          <Callout tone="info">
            I tempi di risposta sono misurati in memoria dall'ultimo avvio del servizio
            ({formatDateTime(metrics.processStartedAt)}) e si azzerano a ogni riavvio. Gli obiettivi sono provvisori e
            i percentili sono stimati al limite superiore del bucket di latenza.
          </Callout>

          {metrics.availabilityHistory?.enabled && (
            metrics.availabilityHistory.available ? (
              <StatGrid>
                {[metrics.availabilityHistory.last24Hours, metrics.availabilityHistory.last7Days, metrics.availabilityHistory.last30Days].map(w => (
                  <Tile
                    key={w.windowMinutes}
                    label={`Disponibilità ${w.label}`}
                    value={w.percent === null ? '—' : `${w.percent.toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`}
                    sub={w.percent === null
                      ? 'nessun campione salvato'
                      : `${w.upMinutes} su, ${w.downMinutes} giù, ${w.noSampleMinutes} senza campione (conta come fermo)`}
                    tone={w.percent === null ? 'muted' : w.percent >= 99 ? 'ok' : 'bad'}
                  />
                ))}
              </StatGrid>
            ) : (
              <Callout tone="warning">Storico della disponibilità non raggiungibile.</Callout>
            )
          )}

          <StatGrid>
            <Tile
              label="Disponibilità dall'avvio"
              value={metrics.availability.percent === null ? '—' : `${metrics.availability.percent.toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`}
              sub={`${metrics.availability.upMinutes} su ${metrics.availability.sampledMinutes} minuti campionati`}
              tone={metrics.availability.percent === null ? 'muted' : metrics.availability.percent >= 99 ? 'ok' : 'bad'}
            />
            <Tile
              label="Latenza dashboard (P95)"
              value={ms(dashboard?.p95Ms ?? null)}
              sub={dashboard ? `obiettivo ${ms(dashboard.targetMs)}` : undefined}
              tone={dashboard?.withinTarget === null || !dashboard ? 'muted' : dashboard.withinTarget ? 'ok' : 'bad'}
            />
            <Tile
              label="Successo API"
              value={pct(metrics.categories.length ? successOverall(metrics.categories) : null)}
              sub={`${totalRequests.toLocaleString('it-IT')} richieste in finestra`}
            />
            <Tile
              label="Scritture (P95)"
              value={ms(writes?.p95Ms ?? null)}
              sub={writes ? `obiettivo ${ms(writes.targetMs)}` : undefined}
              tone={writes?.withinTarget === null || !writes ? 'muted' : writes.withinTarget ? 'ok' : 'bad'}
            />
            <Tile
              label="API esterne (P95)"
              value={ms(external?.p95Ms ?? null)}
              sub={external ? `${external.requests.toLocaleString('it-IT')} richieste · obiettivo ${ms(external.targetMs)}` : undefined}
              tone={external?.withinTarget === null || !external ? 'muted' : external.withinTarget ? 'ok' : 'bad'}
            />
          </StatGrid>

          <Card variant="outlined" padding="none">
            <CardHeader title="Tempi di risposta per categoria" />
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th className="text-end">Richieste</th>
                  <th className="text-end">P50</th>
                  <th className="text-end">P95</th>
                  <th className="text-end">P99</th>
                  <th className="text-end">Obiettivo P95</th>
                  <th>Esito</th>
                  <th className="text-end">Errori 5xx</th>
                  <th className="text-end">Errori client 4xx</th>
                  <th className="text-end">Successo</th>
                </tr>
              </thead>
              <tbody>
                {metrics.categories.map(c => (
                  <tr key={c.category}>
                    <td>{c.label}</td>
                    <td className="text-end md-num">{c.requests.toLocaleString('it-IT')}</td>
                    <td className="text-end md-num">{ms(c.p50Ms)}</td>
                    <td className="text-end md-num md-typescale-emphasized-body-medium">{ms(c.p95Ms)}</td>
                    <td className="text-end md-num">{ms(c.p99Ms)}</td>
                    <td className="text-end md-num">{ms(c.targetMs)}</td>
                    <td>{targetBadge(c)}</td>
                    <td className="text-end md-num">{pct(c.errorRate)}</td>
                    <td className="text-end md-num">{pct(c.clientErrorRate)}</td>
                    <td className="text-end md-num">{pct(c.successRate)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Card>

          <Card variant="outlined" padding="none">
            <CardHeader title="Rotte più lente (almeno 20 richieste)" />
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Rotta</th>
                  <th>Categoria</th>
                  <th className="text-end">Richieste</th>
                  <th className="text-end">P95</th>
                  <th className="text-end">P99</th>
                  <th className="text-end">Errori 5xx</th>
                </tr>
              </thead>
              <tbody>
                {metrics.slowRoutes.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-3">Nessuna rotta con richieste sufficienti</td></tr>
                )}
                {metrics.slowRoutes.map(r => (
                  <tr key={r.route}>
                    <td><code>{r.route}</code></td>
                    <td>{r.category}</td>
                    <td className="text-end md-num">{r.requests.toLocaleString('it-IT')}</td>
                    <td className="text-end md-num md-typescale-emphasized-body-medium">{ms(r.p95Ms)}</td>
                    <td className="text-end md-num">{ms(r.p99Ms)}</td>
                    <td className="text-end md-num">{pct(r.errorRate)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Card>

          {metrics.availability.lastDown && (
            <div className="text-muted md-typescale-body-small">
              Ultimo minuto non disponibile: {formatDateTime(metrics.availability.lastDown)}
            </div>
          )}
        </>
      )}

      {!unauthorized && (report || jobs || outbox) && (
        <CardGrid min={360}>
          {report && (
            <Card variant="outlined">
              <CardHeader title="Database, disco e backup" />
              <CardContent>
                <KeyValueList
                  className="md-typescale-body-small"
                  columns={2}
                  items={[
                    {
                      label: 'Connessioni al database',
                      value: report.databaseLoad.available && report.databaseLoad.connections
                        ? `${report.databaseLoad.connections.total} su ${report.databaseLoad.connections.max} (${report.databaseLoad.connections.utilizationPercent ?? '—'}%)`
                        : report.databaseLoad.reason ?? 'Non disponibile',
                    },
                    { label: 'Dimensione database', value: bytes(report.databaseLoad.databaseSizeBytes) },
                    {
                      label: 'Disco usato',
                      value: report.storage.disk.available
                        ? `${report.storage.disk.usedPercent ?? '—'}% (liberi ${bytes(report.storage.disk.freeBytes)})`
                        : report.storage.disk.reason ?? 'Non disponibile',
                    },
                    {
                      label: 'Ultimo backup',
                      value: (
                        <>
                          <StatusPill tone={report.backupStatus.state === 'ok' ? 'success' : 'error'}>
                            {BACKUP_LABELS[report.backupStatus.state] ?? report.backupStatus.state}
                          </StatusPill>{' '}
                          {report.backupStatus.lastSuccessAt ? formatDateTime(report.backupStatus.lastSuccessAt) : ''}
                        </>
                      ),
                    },
                  ]}
                />
              </CardContent>
            </Card>
          )}
          {(jobs || outbox) && (
            <Card variant="outlined">
              <CardHeader title="Job e outbox" />
              <CardContent>
                <KeyValueList
                  className="md-typescale-body-small"
                  columns={2}
                  items={[
                    ...(jobs ? [
                      { label: 'Job in coda', value: `${jobs.pending} (il più vecchio da ${ageText(jobs.oldestPendingAgeSeconds)})` },
                      { label: 'Job falliti (dead letter)', value: jobs.deadLetterCount },
                      { label: 'Tasso di fallimento job, 24 ore', value: jobs.jobFailureRatePercent === null ? '—' : `${jobs.jobFailureRatePercent}%` },
                    ] : []),
                    ...(outbox ? [
                      { label: 'Outbox in coda', value: `${outbox.pending} (la più vecchia da ${ageText(outbox.oldestPendingAgeSeconds)})` },
                      { label: 'Outbox fallite', value: outbox.failed },
                    ] : []),
                  ]}
                />
              </CardContent>
            </Card>
          )}
        </CardGrid>
      )}

      {!unauthorized && <ReportingAggregatesSection />}
      {!unauthorized && <CacheSection />}
      {!unauthorized && <SearchIndexSection />}
    </Page>
  )
}

/** Successo complessivo: richieste senza 5xx su tutte le categorie, media pesata sul numero di richieste. */
function successOverall(categories: CategoryMetrics[]): number | null {
  const total = categories.reduce((s, c) => s + c.requests, 0)
  if (total === 0) return null
  const ok = categories.reduce((s, c) => s + (c.successRate ?? 0) * c.requests, 0)
  return ok / total
}
